<mat-card class="detail-card" valumizeCardContextMenu [commentSlug]="'asset-fin-data-history'">
  <mat-card-header class="detail-card-header">
    <mat-card-title data-test="title">Financial History</mat-card-title>
    <valumize-card-comments-button [commentSlug]="'asset-fin-data-history'"></valumize-card-comments-button>
    @if (companyCurrencyIso$ | async; as companyCurrencyIso) {
      <valumize-currency-badge
        kind="COMPANY"
        [iso]="companyCurrencyIso"
        tooltip="Financial figures are reported in the company's currency">
      </valumize-currency-badge>
    }
  </mat-card-header>
  <!-- preserveContent: without it MatTabBodyPortal detaches the inactive tab, destroying
       the metrics table and the financialDataForms FormArray that holds the user's typed
       figures until recordsToSave emits. Switching to Commentary and back would silently
       reset them while the card still looked mid-edit. -->
  <mat-tab-group [preserveContent]="true">
    <mat-tab>
      <!-- Content survives a tab switch (preserveContent above), so an edit left open on the
           inactive tab is invisible: the user cannot see where their unsaved work is, and the
           other tab's edit button is disabled by selectIsAnyAssetFormInEditMode with nothing
           explaining why. Mark the tab that holds the open edit. -->
      <ng-template mat-tab-label>
        Operating Metrics
        @if (isEditable) {
          <mat-icon class="tab-edit-indicator"
                    data-test="operating-metrics-edit-indicator"
                    matTooltip="Unsaved changes on this tab — save or cancel them to edit elsewhere">edit</mat-icon>
        }
      </ng-template>
      @if ((selectedAssetFinancialHistory$ | async); as assetHistory) {
        <mat-card-subtitle>
          @if (assetHistory.status === 'ERROR') {
            <valumize-info-panel [appearance]="'ERROR'" [message]="assetHistory.errorMessage"></valumize-info-panel>
          }
          @if (assetHistory.status === 'SAVED') {
            <valumize-info-panel [appearance]="'SAVED'" [message]="'Financial History successfully saved'"></valumize-info-panel>
          }
          @if (assetHistory.status === 'LOADING') {
            <mat-progress-spinner></mat-progress-spinner>
          }
        </mat-card-subtitle>
        <valumize-asset-fin-data-history-table
          [selectedAssetFinancialPerformanceData]="selectedAssetFinancialHistoryData"
          [baselineAssetFinancialPerformanceData]="baselineAssetFinancialHistoryData"
          [isEditable]="isEditable"
          [isDataset]="isDataset"
          [isEditDisabled]="(isEditDisabled$ | async)"
          (periodTypesChange)="onPeriodTypesChange($event)"
          (gpProjectedFilterChange)="onGpProjectedFilterChange($event)"
          (recordsToSave)="onSave($event)"
          (recordsForCalc)="onCalc($event)"
          (triggerEdit)="onEdit()"
          (triggerCancel)="onCancel()">
        </valumize-asset-fin-data-history-table>
      }
    </mat-tab>
    <mat-tab>
      <ng-template mat-tab-label>
        Commentary
        @if (recentFinancialPerformanceIsEditable) {
          <mat-icon class="tab-edit-indicator"
                    data-test="commentary-edit-indicator"
                    matTooltip="Unsaved changes on this tab — save or cancel them to edit elsewhere">edit</mat-icon>
        }
      </ng-template>
      <div class="detail-card-content" [formGroup]="recentFinancialPerformanceForm">
        @let reportId = (relatedReportId$ | async);
        @let isEditDisabledForCommentary = (isEditDisabled$ | async);
        @let currentRecentFinancialPerformance = (recentFinancialPerformance$ | async);
        @let currentReportDate = (reportDate$ | async)?.date;
        <!-- This editor's own save state. The tab previously rendered panels only for the read-only
             history slice, so a failed save showed nothing at all — and because the shared record was
             blanked, the card claimed no commentary had ever been recorded. -->
        @if (saveState$ | async; as saveState) {
          @if (saveState.status === 'ERROR') {
            <valumize-info-panel [appearance]="'ERROR'" [message]="saveState.errorMessage"></valumize-info-panel>
          }
          @if (saveState.status === 'SAVED') {
            <valumize-info-panel [appearance]="'SAVED'" [message]="'Commentary successfully saved'"></valumize-info-panel>
          }
        }
        @if (commentaryHistory$ | async; as history) {
          @if (history.status === 'LOADING') {
            <mat-progress-spinner></mat-progress-spinner>
          } @else if (history.status === 'ERROR') {
            <valumize-info-panel [appearance]="'ERROR'" [message]="history.errorMessage"></valumize-info-panel>
          } @else if (history.data.length === 0 && !reportId) {
            <!-- @else if, not a sibling @if: gated only on an empty array this asserted "never been
                 part of a fund report" on first paint, while the request was in flight, and again
                 next to the error panel after it failed. -->
            <div class="no-text" style="color: grey">No commentary recorded yet — this asset has never been part of a fund report.</div>
          }
          <div class="recent-financial-performance-history">
            <!-- The current report's entry renders from the fundInvestment slice, independent of the
                 history read-model. It used to live inside the @for below, so a failed history GET
                 left the tab read-only with no way to enter or correct commentary. -->
            @if (reportId) {
              <div class="key-value-pair-column" data-test="current-report-commentary">
                <!-- Label left, modification date and actions right. The date belongs on this row
                     rather than in a card footer: it is this record's modDate, not the card's, and a
                     mat-card-actions footer inside a tab is `position: absolute` (_cards.scss), which
                     hangs outside the tab body's height:100%/overflow:auto box and put a scrollbar on
                     the Commentary tab from the first paint. -->
                <div class="key-value-pair-column-title title-with-actions">
                  <span>
                    Recorded on Report Date: @if (currentReportDate) {
                      {{ currentReportDate | date }}
                    } @else {
                      <span style="color: grey">not stated</span>
                    }
                  </span>
                  <span class="row-actions recent-financial-performance-actions">
                    <!-- reportId is guaranteed here: this whole block is inside @if (reportId). -->
                    <span class="last-modified" data-test="recent-financial-performance-modification-date">Last modified: {{ currentRecentFinancialPerformance?.modDate | timestamp }}</span>
                    @if (!recentFinancialPerformanceIsEditable) {
                      <button (click)="editRecentFinancialPerformance()" [disabled]="isEditDisabledForCommentary!" color="primary" data-test="recent-financial-performance-edit-button" mat-icon-button>
                        <mat-icon>edit</mat-icon>
                      </button>
                    }
                    @if (recentFinancialPerformanceIsEditable) {
                      <button (click)="saveRecentFinancialPerformance()" [disabled]="recentFinancialPerformanceForm.invalid" color="primary" data-test="recent-financial-performance-save-button" mat-icon-button>
                        <mat-icon>save</mat-icon>
                      </button>
                      <button (click)="cancelRecentFinancialPerformance()" color="primary" data-test="recent-financial-performance-cancel-button" mat-icon-button>
                        <mat-icon>cancel</mat-icon>
                      </button>
                    }
                  </span>
                </div>
                @if (recentFinancialPerformanceIsEditable) {
                  <mat-form-field class="description">
                    <textarea class="line-breaks" formControlName="text" data-test="recent-financial-performance-text" matInput></textarea>
                  </mat-form-field>
                } @else if (currentRecentFinancialPerformance?.text) {
                  <div class="line-breaks">{{ currentRecentFinancialPerformance?.text }}</div>
                } @else {
                  <div class="no-text" style="color: grey">No commentary recorded yet.</div>
                }
              </div>
            }
            @for (entry of history.data; track entry.fundReportId) {
              @if (entry.fundReportId !== reportId) {
                <div class="key-value-pair-column">
                  <div class="key-value-pair-column-title">
                    <!-- An undated report (imported without a parseable report date) would otherwise render
                         "Recorded on Report Date:" followed by nothing. -->
                    Recorded on Report Date: @if (entry.reportDate) {
                      {{ entry.reportDate | date }}
                    } @else {
                      <span style="color: grey">not stated</span>
                    }
                  </div>
                  @if (entry.recentFinancialPerformance.text) {
                    <div class="line-breaks">{{ entry.recentFinancialPerformance.text }}</div>
                  } @else {
                    <div class="no-text" style="color: grey">No commentary recorded yet.</div>
                  }
                </div>
              }
            }
          </div>
        }
      </div>
    </mat-tab>
  </mat-tab-group>
</mat-card>
